import React, { useMemo, memo, useCallback, useState, useEffect, useRef } from 'react';
import './MediaControls.css';
import Button from '../../primitives/Button/Button';
import { useMediaPlayerControls } from '../../context/MediaPlayerContext';

import { ReactComponent as VolumeXIcon } from '../../../../assets/icon/feather-volume-x.svg';
import { ReactComponent as Volume0Icon } from '../../../../assets/icon/feather-volume.svg';
import { ReactComponent as Volume1Icon } from '../../../../assets/icon/feather-volume-1.svg';
import { ReactComponent as Volume2Icon } from '../../../../assets/icon/feather-volume-2.svg';
import { ReactComponent as PauseIcon } from '../../../../assets/icon/feather-pause.svg';
import { ReactComponent as PlayIcon } from '../../../../assets/icon/feather-play.svg';
import Slider from '../../primitives/Slider/Slider';
import { YouTubeMedia, SetYouTubeMedia } from '../../../babylon/World/Types';
import { useAppMessage, useParticipantProperty, useLocalSessionId } from '@daily-co/daily-react';

interface MediaControlsProps {
}

const HUDMediaControls: React.FC<MediaControlsProps> = memo(({ }) => {
  const localUserId = useLocalSessionId();
  const isLocalOwner = true; // useParticipantProperty(localUserId, 'owner');
  // const isLocalOwner = useParticipantProperty(localUserId, 'owner');

  const { setVolume, setPlaybackRate, setSeek, setMute, setPause, onUpdatedYouTubeVideoId } = useMediaPlayerControls();

  const [isChangingVolume, setIsChangingVolume] = useState(!isLocalOwner);
  const [isChangingSlider, setIsChangingSlider] = useState(false);

  const [playbackRate, updatePlaybackRate] = useState(1);
  const playbackRates = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
  const getPlaybackRateIndex = () => playbackRates.findIndex(rate => playbackRate === rate);

  const volumeValues = [0,1,2,3,5,7,10,15,20,25,30,35,40,45,50,55,60,70,80,90,100];
  const getVolumeIndex = () => volumeValues.findIndex(vol => vol === volume);
  const [volume, updateVolume] = useState(35);
  const [isMuted, setIsMuted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const [youTubeMedia, setYouTubeMedia] = useState<YouTubeMedia | null>(null);
  const intervalForSeek: any = useRef(null);
  const intervalForVolume: any = useRef(null);
  const [currSeek, setCurrSeek] = useState(0);
  const [seekValue, setSeekValue] = useState(0);

  const sendAppMessage = useAppMessage();


  useEffect(() => {
    const unsubscribeUpdatedYouTubeVideoId = onUpdatedYouTubeVideoId((media: YouTubeMedia) => {
      setYouTubeMedia(media);
      setCurrSeek(0);
      console.log(media);
      media.player.on();
    });

    return () => {
      unsubscribeUpdatedYouTubeVideoId();
    };

  }, [onUpdatedYouTubeVideoId]);

  useEffect(() => {
    createIntervalForSeek();
    return () => {
      if (intervalForSeek.current) clearInterval(intervalForSeek.current);
    }
  }, [youTubeMedia, playbackRate]);

  useEffect(() => {
    sendYouTubeMessage();
  }, [isPaused, playbackRate, youTubeMedia?.videoId]);

  const createIntervalForSeek = () => {
    if (intervalForSeek.current) clearInterval(intervalForSeek.current)
    intervalForSeek.current = setInterval(async () => {
      if (youTubeMedia) {
        const currentTime = Math.round(await youTubeMedia.player.getCurrentTime());
        setCurrSeek(currentTime);
        if (youTubeMedia.duration > 0) setSeekValue(parseFloat((currentTime / youTubeMedia.duration).toFixed(5)));
        else setSeekValue(1);
      }
    }, 1000 / playbackRate);
  }

  const increasePlaybackRate = async () => {
    const currentIndex = getPlaybackRateIndex();
    const nextIndex = Math.min(currentIndex + 1, playbackRates.length - 1);
    updatePlaybackRate(playbackRates[nextIndex]);
    setPlaybackRate(playbackRates[nextIndex]);
  }

  const handlePlaybackClicked = () => {
    if (!isLocalOwner) return;
    if (playbackRate !== playbackRates[playbackRates.length-1]) {
      increasePlaybackRate();
    } 
    else {
      const updatedRate = playbackRates[0];
      updatePlaybackRate(updatedRate);
      setPlaybackRate(updatedRate);
    }
  }

  const handleVolumeClicked = () => {
    if (isChangingVolume) {
      setMute(!isMuted);
      setIsMuted(!isMuted);
    } else {
      setIsChangingVolume(true);
    }
  }

  const handleSeekClicked = () => {
    if (!isLocalOwner) return;
    setIsChangingVolume(false);
    createIntervalForSeek();
  }

  const handleSlider = (val: number) => {
    if (!isLocalOwner) return;
    console.log('recv slider val: ', val);
    if (isChangingVolume) {
      const updatedVolume = val * 100;
      updateVolume(updatedVolume);
      setVolume(updatedVolume);
    } else {
      if (currSeek && youTubeMedia) {
        youTubeMedia.currentSeek = Math.floor(val * youTubeMedia.duration);
        setCurrSeek(youTubeMedia.currentSeek);
      }
      sendYouTubeMessage();
      setSeek(val);
    }
  }

  const handleSlideDown = () => {
    if (!isLocalOwner) return;
    setIsChangingSlider(true);
    clearInterval(intervalForSeek.current);
  }

  const handleSlideUp = () => {
    if (!isLocalOwner) return;
    setIsChangingSlider(false);
    createIntervalForSeek();
  }

  const handlePauseClicked = () => {
    setIsPaused((prev) => {
      const willBePaused = !prev;
      if (willBePaused) clearInterval(intervalForSeek.current);
      else createIntervalForSeek();
      setPause(willBePaused);
      return willBePaused;
    })
  }

  const sendYouTubeMessage = () => {
    if (youTubeMedia === null) return;
    const message: SetYouTubeMedia = {
      messageType: 'world',
      worldMessageType: 'set-youtube-media',
      isPaused,
      playbackRate,
      currentSeek: currSeek,
      videoId: youTubeMedia.videoId,
    };
    sendAppMessage(message);
  }

  const formatTime = (time: number) => {
    const hours = Math.floor(time / (60 * 60));
    const timeMinusHours = time - (hours * 60 * 60);
    const minutes = Math.floor(timeMinusHours / 60);
    const seconds = timeMinusHours - minutes * 60;
    return `${hours > 0 ? hours + ':' : ''}${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  if (!youTubeMedia?.player) return null;

  return (
    <div className="hud-media-controls-container">
      <div className="slider-container">
        <div className="left slider-label" onClick={handleSeekClicked}>{ formatTime(currSeek) }</div>
        <Slider initialValue={isChangingVolume ? volume / 100 : seekValue} step={0.0001} onChange={handleSlider} onPointerDown={handleSlideDown} onPointerUp={handleSlideUp} />
        {/* <div className="right slider-label">{ youTubeMedia?.duration ? formatTime(youTubeMedia.duration) : '00:00' }</div> */}
      <Button size="tiny"
              icon={isMuted || volume === 0 ? <VolumeXIcon /> : volume >= 50 ? <Volume2Icon /> : volume >= 15 ? <Volume1Icon /> : <Volume0Icon />}
              onClick={handleVolumeClicked}
      />
      <Button size="tiny" icon={ isPaused ? <PlayIcon /> : <PauseIcon /> } onClick={handlePauseClicked} isDisabled={!isLocalOwner} />
      <Button size="tiny" label={`${playbackRate}x`} onClick={handlePlaybackClicked} isDisabled={!isLocalOwner} />
      </div>
    </div>
  );
});

export default HUDMediaControls;
